import { Outlet, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import {
  LayoutDashboard, Megaphone, MessageSquare, Users, Building2,
  BarChart3, Settings, Bell, Search, ChevronDown, Menu, X,
  Zap, LogOut
} from "lucide-react";
import { Badge } from "@/components/ui/badge";

const NAV_ITEMS = [
  { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
  { to: "/campaigns", icon: Megaphone, label: "Campaigns" },
  { to: "/conversations", icon: MessageSquare, label: "Conversations", badge: 5 },
  { to: "/leads", icon: Users, label: "Leads" },
  { to: "/properties", icon: Building2, label: "Properties" },
  { to: "/analytics", icon: BarChart3, label: "Analytics" },
  { to: "/settings", icon: Settings, label: "Settings" },
];

const PAGE_TITLES = {
  "/dashboard": "Dashboard",
  "/campaigns": "Campaigns",
  "/conversations": "Conversations",
  "/leads": "Leads Pipeline",
  "/properties": "Properties",
  "/analytics": "Analytics",
  "/settings": "Settings",
};

export default function AppLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const pageTitle = PAGE_TITLES[location.pathname] || "PropVision";

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/30 z-20 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-30 flex flex-col w-64 bg-white border-r border-slate-200 transform transition-transform duration-200 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        {/* Logo */}
        <div className="flex items-center gap-3 px-5 h-16 border-b border-slate-200 shrink-0">
          <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center">
            <Zap className="w-4 h-4 text-white" strokeWidth={2.5} />
          </div>
          <div>
            <p className="font-heading font-bold text-slate-900 text-sm leading-none">PropVision</p>
            <p className="text-xs text-slate-400 mt-0.5">by CodeIntel AI Labs</p>
          </div>
          <button
            className="ml-auto lg:hidden text-slate-400 hover:text-slate-700"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Company badge */}
        <div className="mx-4 mt-4 mb-2 px-3 py-2.5 rounded-lg bg-blue-50 border border-blue-100">
          <p className="text-xs text-blue-500 font-semibold uppercase tracking-wide">Demo Account</p>
          <p className="text-sm font-semibold text-slate-800 mt-0.5">Elite Estate</p>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 py-2 space-y-0.5 overflow-y-auto" data-testid="sidebar-nav">
          {NAV_ITEMS.map(({ to, icon: Icon, label, badge }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                `sidebar-link ${isActive ? "active" : ""}`
              }
              data-testid={`nav-${label.toLowerCase().replace(" ", "-")}`}
              onClick={() => setSidebarOpen(false)}
            >
              <Icon className="w-4 h-4 shrink-0" />
              <span className="flex-1">{label}</span>
              {badge && (
                <span className="text-xs bg-blue-600 text-white rounded-full px-1.5 py-0.5 font-semibold leading-none">
                  {badge}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        {/* User */}
        <div className="px-3 pb-4 pt-2 border-t border-slate-100">
          <div className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-slate-50 cursor-pointer">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-xs font-bold shrink-0">
              RM
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-800 truncate">Rajesh Mehta</p>
              <p className="text-xs text-slate-400 truncate">Admin · Elite Estate</p>
            </div>
            <LogOut
              className="w-4 h-4 text-slate-400 hover:text-slate-600 cursor-pointer"
              onClick={() => navigate("/")}
              data-testid="logout-btn"
            />
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Topbar */}
        <header className="h-16 bg-white border-b border-slate-200 flex items-center gap-4 px-6 shrink-0 z-10">
          <button
            className="lg:hidden text-slate-500 hover:text-slate-800"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="w-5 h-5" />
          </button>

          <div>
            <h1 className="font-heading font-semibold text-slate-900 text-base leading-none" data-testid="page-title">
              {pageTitle}
            </h1>
          </div>

          <div className="flex-1 max-w-sm ml-4 hidden md:block">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search leads, properties..."
                className="w-full pl-9 pr-4 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-300 placeholder:text-slate-400"
                data-testid="topbar-search"
              />
            </div>
          </div>

          <div className="flex items-center gap-2 ml-auto">
            <button
              className="relative p-2 rounded-lg hover:bg-slate-100 text-slate-500 transition-colors"
              data-testid="notifications-btn"
            >
              <Bell className="w-5 h-5" />
              <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full" />
            </button>

            <div
              className="flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-lg hover:bg-slate-100 cursor-pointer transition-colors"
              data-testid="user-menu"
            >
              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-xs font-bold">
                RM
              </div>
              <span className="text-sm font-medium text-slate-700 hidden sm:block">Rajesh</span>
              <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto" data-testid="main-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
